import { create } from 'zustand'
import { createCardWithdrawApi } from '../services/uzsWithdrawApi'
import useAppStore from '../../../store/useAppStore'

/**
 * UZS Withdraw Store
 * Zustand store для управления состоянием вывода кристаллов на узбекскую карту
 *
 * Поддерживает поллинг активной заявки (каждые 12 секунд),
 * пока статус не станет финальным (success / rejected / cancelled / stuck)
 */

const POLLING_INTERVAL = 12000 // 12 секунд

export const createCardWithdrawStore = (currency) => {
  const cardApi = createCardWithdrawApi(currency)
  return create((set, get) => ({
  // Данные экрана из GET /api/uzs-withdraw/
  info: null,         // { enabled, can_withdraw, has_active, has_uzs_deposit, crystals_per_1000_uzs_net, min_crystals, max_crystals, min_uzs, max_uzs, last_card_number, last_card_owner, last_bank_name }
  history: [],        // последние заявки

  // Состояния загрузки
  isLoading: false,
  isSubmitting: false,
  error: null,

  // ID таймера поллинга
  _pollingTimer: null,

  /**
   * Загрузить данные экрана вывода (info + history)
   */
  loadData: async () => {
    set({ isLoading: true, error: null })

    try {
      const data = await cardApi.fetchData()
      set({
        info: data.info || null,
        history: data.history || [],
        isLoading: false,
      })

      // Если есть активная заявка — запускаем поллинг
      if (data.info?.has_active) {
        get().startPolling()
      }

      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки данных вывода UZS:', error)
      set({
        isLoading: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Создать заявку на вывод в UZS
   * @param {Object} params — { card_number, card_owner, bank_name, crystals }
   * @returns {Promise<Object>} результат { id, uzs, crystals, status }
   */
  submitCardWithdraw: async ({ card_number, card_owner, bank_name, crystals }) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await cardApi.create({ card_number, card_owner, bank_name, crystals })
      set({ isSubmitting: false })

      // Перезагружаем данные чтобы получить обновлённую историю и has_active
      try {
        await get().loadData()
      } catch {
        set((state) => ({
          info: state.info ? { ...state.info, has_active: true } : null,
          history: [{ ...result, card_number, card_owner, bank_name: state.info?.bank_name || bank_name }, ...state.history],
        }))
        get().startPolling()
      }

      return result
    } catch (error) {
      console.error('❌ Ошибка создания заявки вывода UZS:', error)
      set({
        isSubmitting: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Запустить поллинг статуса активной заявки
   */
  startPolling: () => {
    // Останавливаем предыдущий таймер если был
    get().stopPolling()

    const timer = setInterval(async () => {
      const { info } = get()

      // Если нет активной заявки — останавливаем поллинг
      if (!info?.has_active) {
        get().stopPolling()
        return
      }

      try {
        const data = await cardApi.fetchData()
        set({
          info: data.info || null,
          history: data.history || [],
        })

        // Если активная заявка перешла в финальный статус — останавливаем поллинг и обновляем баланс
        if (!data.info?.has_active) {
          get().stopPolling()
          // Обновляем баланс — при reject/cancel кристаллы возвращаются
          useAppStore.getState().loadProfile?.()
        }
      } catch (error) {
        console.error('❌ Ошибка поллинга вывода UZS:', error)
        // При ошибке не останавливаем поллинг — попробуем снова
      }
    }, POLLING_INTERVAL)

    set({ _pollingTimer: timer })
  },

  /**
   * Остановить поллинг
   */
  stopPolling: () => {
    const { _pollingTimer } = get()
    if (_pollingTimer) {
      clearInterval(_pollingTimer)
      set({ _pollingTimer: null })
    }
  },

  /**
   * Сбросить состояние store
   */
  reset: () => {
    get().stopPolling()
    set({
      info: null,
      history: [],
      isLoading: false,
      isSubmitting: false,
      error: null,
    })
  },
  }))
}

const useUzsWithdrawStore = createCardWithdrawStore('uzs')

export default useUzsWithdrawStore
